/**
 * FontPreviewSelect — Custom dropdown that renders each font option in its own typeface.
 * System/Serif/Mono use local fonts. Google Fonts (Spark, Instrument, DM Sans, etc.)
 * dynamically inject <link> tags for preview purposes.
 */
import { useState, useRef, useEffect } from 'react';
import { fontStacks } from '@/data/font-stacks';
import type { FontStack } from '@/data/font-stacks';

interface FontPreviewSelectProps {
  value: string;
  onChange: (key: string) => void;
}

// Track which Google Font links have been injected
const injectedFonts = new Set<string>();

function ensureFontLoaded(stack: FontStack) {
  if (!stack.googleFontsHref || injectedFonts.has(stack.googleFontsHref)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = stack.googleFontsHref;
  document.head.appendChild(link);
  injectedFonts.add(stack.googleFontsHref);
}

export function FontPreviewSelect({ value, onChange }: FontPreviewSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Inject font links for all entries on mount so previews render
  useEffect(() => {
    Object.values(fontStacks).forEach(ensureFontLoaded);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const selectedStack = fontStacks[value] || fontStacks.system;

  return (
    <div className="font-picker" ref={containerRef}>
      <button
        type="button"
        className="font-picker__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="font-picker__sample" style={{ fontFamily: selectedStack.display }}>
          Aa
        </span>
        <span className="font-picker__name">{selectedStack.name}</span>
        <span className="font-picker__arrow">{isOpen ? '\u25B4' : '\u25BE'}</span>
      </button>

      {isOpen && (
        <div className="font-picker__dropdown">
          {Object.entries(fontStacks).map(([key, stack]) => (
            <button
              key={key}
              type="button"
              className={`font-picker__option ${key === value ? 'is-active' : ''}`}
              onClick={() => {
                onChange(key);
                setIsOpen(false);
              }}
            >
              <span className="font-picker__optSample" style={{ fontFamily: stack.display }}>
                Aa Beispieltext
              </span>
              <span className="font-picker__optMeta">
                <span className="font-picker__optName">{stack.name}</span>
                <span className="font-picker__optDesc">{stack.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
